import { getVenueSlug } from '@/utils/venueSlug';
import { ApiError } from '@core/api/apiError';

/**
 * Customer-сессия мини-аппа: JWT из POST /auth/telegram (обмен initData).
 * Хранится только в памяти (не в localStorage) и подставляется клиентом ядра в Authorization.
 *
 * С K-002 бэкенд определяет клиента ТОЛЬКО по этой сессии: профиль, баллы, подарки и абонементы
 * без неё отвечают 401 (раньше сервер подставлял клиента по telegramId из запроса). Поэтому вход
 * повторяется при временных сбоях (сеть, 429, 5xx), а на 401 от API ядро просит войти заново
 * (reauthenticate) и повторяет запрос.
 *
 * Вход «сначала номер» (phoneFirst): если карточки с этим Telegram ID в заведении ещё нет, сервер
 * не создаёт её сразу, а отвечает status "phone_required" — мини-апп спрашивает номер, и
 * POST /auth/telegram/link дописывает Telegram ID к карточке, которой человек пользовался через
 * браузер (PWA), либо создаёт новую. Старый бэкенд phoneFirst не знает и сразу отдаёт token.
 */

let _token: string | null = null;
/** Сервер ждёт номер (phone_required), а клиент ещё не прошёл экран номера. */
let _phoneStepPending = false;

export function setTelegramToken(token: string): void {
  _token = token;
  _phoneStepPending = false;
}

export function getTelegramToken(): string | null {
  return _token;
}

export function clearTelegramToken(): void {
  _token = null;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

/** Паузы перед повторами входа: временный сбой не должен оставлять клиента без сессии. */
const AUTH_RETRY_DELAYS_MS = [600, 1800];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Повторять имеет смысл только временные отказы; 4xx (неверная initData, нет заведения) — нет. */
function isRetryableStatus(status: number): boolean {
  return status === 429 || status >= 500;
}

function postJSON(path: string, body: unknown): Promise<Response> {
  return fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Venue-Slug': getVenueSlug(),
    },
    body: JSON.stringify(body),
  });
}

interface AuthPayload {
  status?: 'authenticated' | 'phone_required';
  token?: string;
  linked?: boolean;
  merged?: boolean;
}

interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: { code?: string; message?: string };
}

async function readEnvelope<T>(res: Response): Promise<Envelope<T> | null> {
  try {
    return (await res.json()) as Envelope<T>;
  } catch {
    return null;
  }
}

export type TelegramAuthStatus = 'authenticated' | 'phone_required' | 'failed';

/**
 * POST /auth/telegram: обменивает initData на JWT и кладёт его в память.
 * Повторяет до трёх раз при сетевой ошибке, 429 и 5xx.
 *
 * phoneFirst: true — новый клиент получает 'phone_required' (карточка не создана, токена нет).
 * Ответ без status (старый бэкенд) — обычный вход по token.
 *
 * Слаг — тем же резолвером, что и остальное приложение: свой упрощённый вариант разъезжался с ним
 * (реферальный start_param «melman_coffee--ref123», вход через меню бота без ?venue=) — сервер отвечал
 * VENUE_NOT_FOUND, и сессия не выдавалась.
 */
export async function authenticateWithTelegram(
  initData: string,
  options: { phoneFirst?: boolean } = {}
): Promise<TelegramAuthStatus> {
  const body = options.phoneFirst ? { initData, phoneFirst: true } : { initData };
  for (let attempt = 0; ; attempt++) {
    let retryable = false;
    try {
      const res = await postJSON('/auth/telegram', body);
      if (res.ok) {
        const env = await readEnvelope<AuthPayload>(res);
        if (env?.success && env.data?.status === 'phone_required') {
          _phoneStepPending = true;
          return 'phone_required';
        }
        if (env?.success && env.data?.token) {
          setTelegramToken(env.data.token);
          return 'authenticated';
        }
      }
      retryable = isRetryableStatus(res.status);
      console.warn('[telegramAuth] /auth/telegram failed', { status: res.status, attempt });
    } catch (err) {
      retryable = true;
      console.warn('[telegramAuth] /auth/telegram network error', { attempt, err });
    }
    if (!retryable || attempt >= AUTH_RETRY_DELAYS_MS.length) {
      return 'failed';
    }
    await sleep(AUTH_RETRY_DELAYS_MS[attempt]);
  }
}

/** Доказательство номера для /auth/telegram/link — или отказ его давать. */
export type TelegramLinkProof =
  /** event.response из WebApp.requestContact — подписанный Telegram контакт этого пользователя. */
  | { contact: string }
  /** Номер и последние 4 цифры звонка после POST /phone-verification/initiate. */
  | { phone: string; code: string }
  | { skip: true };

export interface TelegramLinkResult {
  /** Telegram ID дописан к карточке, которой клиент пользовался через браузер. */
  linked: boolean;
  /** История второй карточки перенесена в эту. */
  merged: boolean;
  /** Сервер не знает /auth/telegram/link (старый бэкенд) — выполнен обычный вход. */
  legacyFallback: boolean;
}

/**
 * POST /auth/telegram/link — завершает вход «сначала номер»: по доказанному номеру находит карточку
 * PWA и дописывает к ней Telegram ID (linked), иначе создаёт карточку; skip — карточка без номера.
 * Без автоповторов: код из звонка одноразовый, ошибку показываем клиенту (ApiError с кодом сервера).
 * 404 — старый бэкенд без этого маршрута: откат на обычный вход.
 */
export async function linkTelegramAccount(initData: string, proof: TelegramLinkProof): Promise<TelegramLinkResult> {
  let res: Response;
  try {
    res = await postJSON('/auth/telegram/link', { initData, ...proof });
  } catch {
    throw new ApiError('Нет связи с сервером', 'NETWORK_ERROR');
  }

  if (res.status === 404) {
    const legacy = await authenticateWithTelegram(initData);
    if (legacy === 'authenticated') {
      return { linked: false, merged: false, legacyFallback: true };
    }
    throw new ApiError('Не удалось войти', 'LEGACY_LOGIN_FAILED', 404);
  }

  const env = await readEnvelope<AuthPayload>(res);
  if (!res.ok || !env?.success || !env.data?.token) {
    throw new ApiError(
      env?.error?.message || 'Не удалось войти',
      env?.error?.code || (env ? 'UNKNOWN_ERROR' : 'JSON_PARSE_ERROR'),
      res.status
    );
  }

  setTelegramToken(env.data.token);
  return { linked: Boolean(env.data.linked), merged: Boolean(env.data.merged), legacyFallback: false };
}

type PhoneRequiredListener = () => void;
const phoneRequiredListeners = new Set<PhoneRequiredListener>();

/**
 * Подписка на «сервер просит номер» при повторном входе: первый вход не удался (сеть), а карточки
 * у клиента ещё нет — тогда экран номера показывается сейчас. Для существующей карточки повторный
 * вход отдаёт токен, и экран не появляется.
 */
export function onPhoneRequired(listener: PhoneRequiredListener): () => void {
  phoneRequiredListeners.add(listener);
  return () => {
    phoneRequiredListeners.delete(listener);
  };
}

/** Не долбить /auth/telegram на каждый 401, если вход только что не удался. */
const REAUTH_COOLDOWN_MS = 10_000;

let reauthInFlight: Promise<boolean> | null = null;
let lastReauthFailedAt = 0;

/**
 * Повторный вход по актуальной initData WebApp (ядро зовёт его на 401 от API и повторяет запрос).
 * Параллельные 401 делят один вход. Если initData старше суток, сервер её не примет — тогда false,
 * и клиенту нужно открыть мини-апп заново.
 *
 * Тоже phoneFirst: повторный вход не должен молча создавать карточку, пока клиент не ответил на
 * вопрос о номере. Пока открыт экран номера, повторного входа нет вовсе.
 */
export function reauthenticateTelegram(): Promise<boolean> {
  if (reauthInFlight) {
    return reauthInFlight;
  }
  const initData = window.Telegram?.WebApp?.initData;
  if (!initData || _phoneStepPending || Date.now() - lastReauthFailedAt < REAUTH_COOLDOWN_MS) {
    return Promise.resolve(false);
  }
  reauthInFlight = authenticateWithTelegram(initData, { phoneFirst: true })
    .then((status) => {
      if (status === 'phone_required') {
        phoneRequiredListeners.forEach((listener) => listener());
      }
      if (status !== 'authenticated') {
        lastReauthFailedAt = Date.now();
      }
      return status === 'authenticated';
    })
    .finally(() => {
      reauthInFlight = null;
    });
  return reauthInFlight;
}
